/** XeAgents.ai**/
import { COUNTRIES, countryFromTimezone, normalizeCountry } from "@shared/countries";
import { PRICING_REGION_STORAGE_KEY } from "@/hooks/usePricingRegion";

/**
 * The country the register form starts on.
 *
 * It used to start on "US" for everybody. That quietly undid the landing
 * page's geo pricing: a visitor who had just been quoted in INR clicked
 * "Get started", left the dropdown where it was, and the organization was
 * created with country US - so /api/plans then quoted them the USD list and
 * they were billed in dollars. The two surfaces disagreed about where the
 * customer was, and the one that decided the invoice was the one guessing.
 *
 * Strongest signal wins, and all three resolve synchronously so the field is
 * already right on first paint:
 *
 *  1. A currency the visitor picked themselves on the pricing grid. They said
 *     it in as many words, which beats anything inferred.
 *  2. Their time zone, mapped through the ten countries signup offers.
 *  3. The region subtag on their browser languages ("en-IN" -> IN), if it
 *     names one of those ten.
 *
 * Falls back to "US" only when none of them answers, which is where the field
 * used to start unconditionally - so this can only improve on the old default,
 * never do worse. It is still a pre-filled guess in a required, visible field
 * the visitor can change, not a decision made for them.
 */
const OFFERED = new Set(COUNTRIES.map((c) => c.value));

export const DEFAULT_SIGNUP_COUNTRY = "US";

function storedPricingCountry(): string | null {
  try {
    const stored = window.localStorage.getItem(PRICING_REGION_STORAGE_KEY);
    // The pricing grid stores a price list ('IN' / 'US'), and both happen to be
    // countries we offer. 'US' there means "not India" rather than "America",
    // so it is only ever as good as the fallback it replaces.
    return stored === "IN" || stored === "US" ? stored : null;
  } catch {
    // Private mode and blocked site data both throw here.
    return null;
  }
}

function timezoneCountry(): string | null {
  try {
    return countryFromTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone || null);
  } catch {
    return null;
  }
}

function localeCountry(): string | null {
  const locales = navigator.languages?.length
    ? Array.from(navigator.languages)
    : navigator.language
      ? [navigator.language]
      : [];

  for (const locale of locales) {
    const parts = locale.trim().split(/[-_]/);
    for (let i = 1; i < parts.length; i++) {
      const part = parts[i];
      if (part.length === 2 && /^[A-Za-z]{2}$/.test(part)) {
        const code = normalizeCountry(part);
        if (code && OFFERED.has(code)) return code;
      }
    }
  }
  return null;
}

export function detectSignupCountry(): string {
  if (typeof window === "undefined") return DEFAULT_SIGNUP_COUNTRY;
  return (
    storedPricingCountry() ??
    timezoneCountry() ??
    localeCountry() ??
    DEFAULT_SIGNUP_COUNTRY
  );
}

export default detectSignupCountry;
